// MapLibre basemap adapter. Wraps `maplibre-gl` + `pmtiles` and exposes the
// structural `BasemapAdapter` contract from `src/adapters/types.ts`.
//
// Adapter encapsulation rule: nothing outside this file (and `src/core/lod.ts`
// / `src/core/style-resolver.ts`) is allowed to import from `maplibre-gl` or
// `pmtiles`. Consumers of FuseSimple must never see those types in their
// editor's "go to definition", and the sync core must stay engine-agnostic.
//
// Bring-up sequence follows `docs/flows/basemap-lifecycle.md`:
//
//   1. Ensure the `pmtiles` MapLibre protocol is registered exactly once
//      (module-level singleton — see `ensureProtocolRegistered` below).
//   2. Build a `PMTiles` archive for this adapter and `.add()` it to the
//      shared `Protocol` so `pmtiles://<url>` style references resolve.
//   3. Resolve the style (bundled default vs inline object vs URL string),
//      run `{basemapHost}` placeholder substitution, and inject the PMTiles
//      source into the `sources` map so layers can bind to it.
//   4. Construct `new Map({ interactive: false, trackResize: false, … })`.
//      `interactive: false` — Esri is the input driver. `trackResize: false` —
//      FuseSimple forwards size changes explicitly through `resize()`.
//   5. `await` the `"load"` event so the caller's Promise only resolves once
//      the basemap is ready to receive view state.

import type {
  Map as MaplibreMap,
  StyleSpecification as MaplibreStyleSpecification,
} from "maplibre-gl";
import maplibregl from "maplibre-gl";
import { PMTiles, Protocol } from "pmtiles";
import { deriveBasemapHost, resolveStylePlaceholders } from "../core/style-resolver";
import { DebugLogger } from "../debug-logger";
import bundledProtomapsLight from "../styles/protomaps-light.json";
import type {
  BasemapAdapter,
  BasemapFeature,
  LngLat,
  PMTilesHeader,
  ScreenPoint,
  ViewState,
} from "./types";

/**
 * Module-level shared `pmtiles.Protocol` instance.
 *
 * Why module-level instead of per-adapter:
 * - MapLibre's `addProtocol("pmtiles", …)` is a process-global registration.
 *   Calling it twice wires the *second* handler and orphans the first. A
 *   singleton sidesteps that entirely.
 * - Multiple adapter instances (hypothetically — v1 only creates one) can
 *   still coexist because `Protocol.add(pmtiles)` registers archives by URL
 *   inside the same handler. Each adapter owns its `PMTiles` and forgets it
 *   on destroy; the shared `Protocol` itself is never torn down.
 * - `destroy()` therefore does NOT call `removeProtocol` — doing so would
 *   break any *other* live adapter that's still using it. The cost of
 *   leaving the handler registered forever is one tiny closure. See
 *   `FuseSimple_v1_Decisions.md` §"PMTiles protocol registration — ordering".
 */
let sharedProtocol: Protocol | null = null;

function ensureProtocolRegistered(): Protocol {
  if (sharedProtocol) return sharedProtocol;
  sharedProtocol = new Protocol();
  maplibregl.addProtocol("pmtiles", sharedProtocol.tile);
  return sharedProtocol;
}

export interface MapLibreAdapterOptions {
  /**
   * URL (or path) to the PMTiles archive. Passed unchanged to
   * `new PMTiles(...)` and used as the `pmtiles://` source reference the
   * style's `sources` block points at.
   */
  source: string;

  /**
   * Style override. Three shapes are accepted:
   *
   * - `undefined` — use the bundled `protomaps-light.json` stub. The adapter
   *   injects the PMTiles source and resolves `{basemapHost}` placeholders.
   * - `object` — a MapLibre style JSON. Placeholders are resolved; the
   *   PMTiles source is injected only if `sources.protomaps` is missing.
   * - `string` — treated as a style URL and forwarded directly to MapLibre,
   *   which will fetch + parse it. Placeholder substitution is skipped (the
   *   hosting origin is responsible for emitting a fully-resolved style).
   */
  style?: string | MaplibreStyleSpecification;

  /**
   * Optional logger. When omitted, a no-op instance is created so adapter
   * code can call `logger.log(...)` unconditionally without null checks.
   * `FuseSimple.create()` is expected to pass its own logger in so the
   * adapter's output lands under the same `?debug=` flag as the rest of
   * the library.
   */
  logger?: DebugLogger;

  /**
   * Maximum zoom level MapLibre will render to. Should match the LOD
   * ceiling computed from the PMTiles header (typically MAX_OVERZOOM
   * from `lod.ts`). When omitted, MapLibre's default (22) applies.
   * `FuseSimple.create()` sets this from the computed LOD array so the
   * two engines share the same zoom ceiling.
   */
  maxZoom?: number;
}

/**
 * Name of the style source the bundled Protomaps Light stub binds every
 * vector layer to. Matches the `"source": "protomaps"` references in
 * `src/styles/protomaps-light.json`. If a caller passes a style object
 * that already defines a source by this name we leave it alone.
 */
const PMTILES_SOURCE_NAME = "protomaps";

/**
 * Basemap adapter backed by MapLibre GL JS + PMTiles. Owns the hidden
 * MapLibre `Map` instance, the PMTiles archive, and the basemap style.
 * The consumer never touches MapLibre directly — all interaction flows
 * through the `BasemapAdapter` contract defined in `types.ts`.
 *
 * Lifecycle: `new → mount() → setViewState() / resize() / query… → destroy()`.
 * Calling `destroy()` during `mount()` is safe; an `isDestroyed` flag
 * is checked at every async yield point.
 */
export class MapLibreBasemapAdapter implements BasemapAdapter {
  private readonly options: MapLibreAdapterOptions;
  private readonly logger: DebugLogger;

  private map: MaplibreMap | null = null;
  private archive: PMTiles | null = null;
  private cachedHeader: PMTilesHeader | null = null;
  private container: HTMLElement | null = null;
  private maxZoom: number | undefined;

  /**
   * Flipped to `true` in `destroy()`. Checked at every `await` point in
   * `mount()` so a consumer that calls `destroy()` mid-mount unwinds
   * cleanly instead of leaving a half-initialized MapLibre instance
   * attached to a detached container. See `docs/flows/basemap-lifecycle.md`
   * §"Destroy during mount".
   */
  private isDestroyed = false;

  constructor(options: MapLibreAdapterOptions) {
    this.options = options;
    this.logger =
      options.logger ??
      new DebugLogger({
        name: "FUSESIMPLE",
        features: ["LIFECYCLE", "HEADER", "STYLE"],
      });
  }

  /**
   * Create the MapLibre `Map` inside `container`, resolve the basemap
   * style (bundled default, inline object, or URL), inject the PMTiles
   * source, and wait for the `"load"` event. Throws on double-mount or
   * post-destroy calls. Safe to call `destroy()` while this is in
   * flight — the `isDestroyed` flag is checked after each `await`.
   */

  /**
   * Set the maximum zoom level before `mount()`. Must be called before
   * `mount()` — the value is read during Map construction.
   */
  setMaxZoom(zoom: number): void {
    this.maxZoom = zoom;
  }

  async mount(container: HTMLElement): Promise<void> {
    if (this.map) {
      throw new Error("MapLibreBasemapAdapter.mount() called twice on the same adapter instance");
    }
    if (this.isDestroyed) {
      throw new Error("MapLibreBasemapAdapter.mount() called after destroy()");
    }

    this.container = container;
    this.logger.log("LIFECYCLE", {
      message: "mount start",
      source: this.options.source,
    });

    // Step 1: register the shared pmtiles protocol before constructing the
    // Map. MapLibre resolves `pmtiles://` URLs at Map construction time, so
    // the ordering here is load-bearing — `addProtocol` must run first.
    const protocol = ensureProtocolRegistered();

    // Step 2: hand this adapter's archive to the shared protocol.
    // `Protocol.add()` keys by `pmtiles.source.getKey()` (the archive URL),
    // which matches the `pmtiles://<url>` reference the style will use below.
    // If `readHeader()` was called before `mount()`, the archive already
    // exists — reuse it so we share the cached header and HTTP connection.
    if (!this.archive) {
      this.archive = new PMTiles(this.options.source);
    }
    protocol.add(this.archive);

    // Step 3: resolve the style.
    const style = this.resolveStyle();
    this.logger.log("STYLE", {
      message: "style resolved",
      shape:
        typeof this.options.style === "string" ? "url" : this.options.style ? "object" : "bundled",
    });

    if (this.isDestroyed) {
      // Consumer yanked us between style resolution and Map construction.
      // Nothing to tear down yet — just bail before we create a Map we'd
      // immediately have to destroy.
      this.logger.log("LIFECYCLE", { message: "mount aborted (destroyed pre-Map)" });
      return;
    }

    // Step 4: construct the Map. `interactive: false` / `trackResize: false`
    // per `FuseSimple_v1_Decisions.md` — Esri drives input, and the sync
    // core forwards size changes through `resize()` explicitly.
    // Prefer the value set via setMaxZoom(), fall back to the options.
    const maxZoom = this.maxZoom ?? this.options.maxZoom;
    this.map = new maplibregl.Map({
      container,
      style,
      interactive: false,
      trackResize: false,
      attributionControl: false,
      ...(maxZoom !== undefined ? { maxZoom } : {}),
    });
    this.logger.log("LIFECYCLE", {
      message: "MapLibre map created",
      maxZoom: maxZoom ?? "default (22)",
    });

    // Step 5: wait for the first `load` event. If `destroy()` fires before
    // it arrives we still need to resolve (mount is contractually a
    // Promise<void>) so the caller can proceed to its own cleanup.
    await new Promise<void>((resolve) => {
      const map = this.map;
      if (!map) {
        resolve();
        return;
      }

      const onLoad = () => {
        map.off("load", onLoad);
        resolve();
      };
      map.on("load", onLoad);
    });

    if (this.isDestroyed) {
      // Destroyed between `new Map()` and `"load"`. `destroy()` already
      // removed the map; we just log and return without throwing so the
      // caller's `await adapter.mount(...)` resolves cleanly.
      this.logger.log("LIFECYCLE", { message: "mount completed after destroy — no-op" });
      return;
    }

    this.logger.log("LIFECYCLE", { message: "mount complete" });
  }

  /**
   * Push a new center/zoom/bearing to MapLibre. Uses `jumpTo()` for the
   * transform update (fires `move` events for the tile pipeline) then
   * `redraw()` for a synchronous canvas paint to minimize inter-engine
   * drift during active panning.
   */
  setViewState(state: ViewState): void {
    if (!this.map) return;
    // Two-step sync to eliminate the one-frame drift between Esri and
    // MapLibre during active panning:
    //
    // 1. `jumpTo()` updates the transform synchronously and fires move
    //    events so MapLibre's tile pipeline knows to fetch new tiles.
    //    But the canvas paint is deferred to the next rAF — one frame late.
    //
    // 2. `redraw()` forces a synchronous canvas paint, cancelling the
    //    pending rAF. Both engines now reflect the same position in the
    //    same browser composite frame. This is the pattern maplibre-gl-
    //    leaflet couldn't use (Leaflet's render model differs), but it's
    //    available to us because Esri's `view.watch` fires synchronously.
    //
    // Risk: `redraw()` runs the full MapLibre render pipeline inline.
    // If this causes frame drops on heavy styles, we can fall back to
    // `jumpTo()` alone and accept the one-frame lag.
    this.map.jumpTo({
      center: [state.center.lng, state.center.lat],
      zoom: state.zoom,
      bearing: state.bearing,
    });
    this.map.redraw();
  }

  /** Debug: read MapLibre's current rendered center. */
  _getRenderedCenter(): LngLat | null {
    if (!this.map) return null;
    const c = this.map.getCenter();
    return { lng: c.lng, lat: c.lat };
  }

  /** Debug: read MapLibre's current rendered zoom. */
  _getRenderedZoom(): number | null {
    if (!this.map) return null;
    return this.map.getZoom();
  }

  /** Debug: read MapLibre canvas CSS dimensions and center pixel. */
  _getCanvasInfo(): {
    width: number;
    height: number;
    centerX: number;
    centerY: number;
  } | null {
    if (!this.map) return null;
    const canvas = this.map.getCanvas();
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    return { width: w, height: h, centerX: w / 2, centerY: h / 2 };
  }

  /** Resize the basemap container and trigger a MapLibre resize. */
  resize(width: number, height: number): void {
    if (!this.map || !this.container) return;
    // Caller already rounded. See `FuseSimple_v1_Decisions.md` §"CSS
    // translate rounding — blurry tiles fix" for why rounding happens in
    // the sync core and not here.
    this.container.style.width = `${width}px`;
    this.container.style.height = `${height}px`;
    this.map.resize();
  }

  /**
   * Query MapLibre's rendered features at a screen point. Normalizes
   * results into the engine-agnostic `BasemapFeature` shape so
   * consumers never see `maplibre-gl` types.
   */
  queryFeaturesAt(point: ScreenPoint): BasemapFeature[] {
    if (!this.map) return [];
    const hits = this.map.queryRenderedFeatures([point.x, point.y]);
    // Normalize out of MapLibre's `MapGeoJSONFeature` shape so downstream
    // consumers never see maplibre-gl types. We keep the source-layer name
    // when present (vector tile convention) and fall back to the stylesheet
    // layer id otherwise.
    return hits.map((hit) => {
      const geomType = hit.geometry.type;
      const feature: BasemapFeature = {
        layer: hit.sourceLayer ?? hit.layer.id,
        properties: { ...hit.properties },
      };
      // Vector tile features are always simple geometries in practice, but
      // GeoJSON's type union includes `GeometryCollection` which we don't
      // surface. Narrow via a positive allow-list so the public
      // `BasemapFeature.geometryType` stays tight.
      if (
        geomType === "Point" ||
        geomType === "LineString" ||
        geomType === "Polygon" ||
        geomType === "MultiPoint" ||
        geomType === "MultiLineString" ||
        geomType === "MultiPolygon"
      ) {
        feature.geometryType = geomType;
      }
      return feature;
    });
  }

  /**
   * Read and cache the PMTiles archive header. Can be called before
   * `mount()` — the archive is lazily initialized if needed. Subsequent
   * calls return the cached result.
   */
  async readHeader(): Promise<PMTilesHeader> {
    if (this.cachedHeader) return this.cachedHeader;
    if (!this.archive) {
      // `readHeader()` before `mount()` is a programming error — but we can
      // still service it lazily, because the header fetch doesn't actually
      // need the MapLibre Map to exist. This matters for callers that want
      // LOD info early so they can size the Esri view before mounting.
      ensureProtocolRegistered();
      this.archive = new PMTiles(this.options.source);
    }

    const raw = await this.archive.getHeader();
    const header: PMTilesHeader = {
      bounds: [raw.minLon, raw.minLat, raw.maxLon, raw.maxLat],
      center: { lng: raw.centerLon, lat: raw.centerLat },
      minZoom: raw.minZoom,
      maxZoom: raw.maxZoom,
    };
    this.cachedHeader = header;
    this.logger.log("HEADER", {
      message: "header parsed",
      bounds: header.bounds,
      center: header.center,
      minZoom: header.minZoom,
      maxZoom: header.maxZoom,
    });
    return header;
  }

  /**
   * Show or hide a basemap style layer. Uses MapLibre's layout property
   * `visibility` which accepts `"visible"` or `"none"`. No-ops if the
   * map isn't mounted or the layer ID doesn't exist in the current style.
   */
  setLayerVisibility(layerId: string, visible: boolean): void {
    if (!this.map) return;
    try {
      this.map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
      this.logger.log("STYLE", {
        message: "layer visibility changed",
        layerId,
        visible,
      });
    } catch {
      // Layer doesn't exist in the style — silently ignore per contract.
      this.logger.log("STYLE", {
        message: "setLayerVisibility: layer not found",
        layerId,
      });
    }
  }

  /**
   * Register a one-shot listener on MapLibre's `idle` event. MapLibre
   * fires `idle` whenever rendering quiesces — all tiles loaded, no
   * pending camera animation, no outstanding style work. The returned
   * unsubscribe cancels the pending listener if `idle` hasn't fired
   * yet (e.g. `FuseSimple.destroy()` runs mid-init).
   *
   * Called after `mount()` resolves. Before mount there's no
   * `MaplibreMap` to bind to; we log and return a noop unsubscribe.
   */
  onceIdle(callback: () => void): () => void {
    if (!this.map) {
      this.logger.log("LIFECYCLE", {
        message: "onceIdle called before mount — returning noop",
      });
      return () => {};
    }
    const map = this.map;
    let fired = false;
    const handler = () => {
      if (fired) return;
      fired = true;
      this.logger.log("LIFECYCLE", { message: "basemap idle" });
      callback();
    };
    map.once("idle", handler);
    return () => {
      if (fired) return;
      fired = true;
      map.off("idle", handler);
    };
  }

  /**
   * Remove the MapLibre map, release the archive reference, and null
   * out internal state. The process-global PMTiles protocol handler
   * is intentionally left in place (other adapters may share it).
   */
  destroy(): void {
    this.isDestroyed = true;

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    // Drop the archive reference but leave it inside `sharedProtocol`. The
    // protocol instance is process-global and may be serving other adapters
    // or future mounts; tearing entries out of it is intentionally not our
    // job. See the comment on `sharedProtocol` for the full rationale.
    this.archive = null;
    this.cachedHeader = null;
    this.container = null;

    this.logger.log("LIFECYCLE", { message: "destroy complete" });
  }

  /**
   * Resolve the style into a concrete shape MapLibre can consume. Pure
   * enough to unit-test in isolation once we decide to — for now it's a
   * private method because everything it touches is already covered by
   * `style-resolver.test.ts` plus the bundled-style fixture.
   */
  private resolveStyle(): string | MaplibreStyleSpecification {
    const { source, style } = this.options;

    // String style: assume the caller knows what they're doing. MapLibre
    // will fetch it and we won't get a chance to resolve placeholders —
    // document-level responsibility per
    // `FuseSimple_v1_Decisions.md` §5 (Bundled Default Style).
    if (typeof style === "string") {
      return style;
    }

    const basemapHost = deriveBasemapHost(source);
    // `style` is either the caller's inline object or the bundled stub.
    // `resolveStylePlaceholders` shallow-clones either way, so we can
    // safely mutate `sources` below without touching the caller's copy.
    const base = (style ?? (bundledProtomapsLight as unknown)) as MaplibreStyleSpecification;
    const resolved = resolveStylePlaceholders(
      base as unknown as Record<string, unknown>,
      basemapHost,
    ) as unknown as MaplibreStyleSpecification;

    // Inject the PMTiles source under the expected name if the style
    // doesn't already define one. Re-clone `sources` so we don't mutate
    // the caller's inline style or the bundled JSON module.
    const existingSources = (resolved.sources ?? {}) as Record<string, unknown>;
    if (!(PMTILES_SOURCE_NAME in existingSources)) {
      resolved.sources = {
        ...existingSources,
        [PMTILES_SOURCE_NAME]: {
          type: "vector",
          url: `pmtiles://${source}`,
        },
      } as MaplibreStyleSpecification["sources"];
    }

    return resolved;
  }
}
